import Dialog from "@/src/components/atom/Dialog";
import Table from "@/src/components/atom/Table";
import useAgGridHeaderRefresh from "@/src/hooks/useAgGridHeaderRefresh";
import useAlert from "@/src/hooks/useAlert";
import { authApi } from "@/src/store/apis/auth";
import { commonApi } from "@/src/store/apis/common";
import { useLazyGetPaymentsQuery } from "@/src/store/apis/subscription";
import {
  PaymentDto,
  PaymentType,
} from "@/src/store/apis/subscription/interface";
import colorSet from "@/src/styles/color";
import { openInvoiceInNewWindow } from "@/src/components/template/pdfs/Invoice/InvoiceContent";
import { isUndefined } from "@/src/utils/is";
import { createUnderLineLinkAction } from "@/src/utils/row-data-util";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { columnPaymentList } from "../../columns/columnPaymentList";
import { calculateDisplayPrice, calculateTax, calculateUnitPrice } from "../../utils/calculateTax";
import { getFormattedTimezoneDateWithOffset } from "@/src/hooks/useCompanyLocalDate";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";

interface PaymentDetailDialogProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

const emptyArray: PaymentDto[] = [];

function PaymentDetailDialog({ open, onOpenChange }: PaymentDetailDialogProps) {
  const { t } = useTranslation();
  const gridRef = useRef<AgGridReact>(null);
  const alert = useAlert();

  const [columnDefs] = useState<ColDef[]>(columnPaymentList);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });
  const [isReady, setIsReady] = useState<boolean>(false);

  // API
  const session = authApi.endpoints.getSession.useQueryState(undefined);
  const { countryList } =
    commonApi.endpoints.getCommonCodeViaCodeName.useQueryState(
      {
        codeName: "COUNTRY",
      },
      {
        selectFromResult: ({ currentData, isFetching, isError }) => {
          const isUnstable = isUndefined(currentData) || isError || isFetching;
          const isStable = !isUnstable;

          return {
            isFetching,
            countryList: isStable ? currentData : [],
          };
        },
      }
    );
  const [getPayments, { isFetching, count, list }] = useLazyGetPaymentsQuery({
    selectFromResult: ({ currentData, isFetching, isError }) => {
      const isUnstable = isUndefined(currentData) || isError || isFetching;
      const isStable = !isUnstable;

      return {
        isFetching,
        count: isStable ? currentData.count : 0,
        list: isStable ? currentData.rows : emptyArray,
      };
    },
  });

  useAgGridHeaderRefresh({
    gridRef: gridRef.current,
    isReady,
    headerSet: [
      {
        columnKey: "createdAt",
        langKey: "subscriptionManagement:paymentList.table.createdAt",
      },
      {
        columnKey: "refundDateAt",
        langKey: "subscriptionManagement:paymentList.table.refundDateAt",
      },
      {
        columnKey: "paymentDetail",
        langKey: "subscriptionManagement:paymentList.table.paymentDetail",
      },
      {
        columnKey: "paymentStatus",
        langKey: "subscriptionManagement:paymentList.table.paymentStatus",
      },
      {
        columnKey: "paymentNo",
        langKey: "subscriptionManagement:paymentList.table.paymentNo",
      },
      {
        columnKey: "cardNumber",
        langKey: "subscriptionManagement:paymentList.table.cardNumber",
      },
      {
        columnKey: "price",
        langKey: "subscriptionManagement:paymentList.table.price",
      },
      {
        columnKey: "tax",
        langKey: "subscriptionManagement:paymentList.table.tax",
      },
      {
        columnKey: "amount",
        langKey: "subscriptionManagement:paymentList.table.amount",
      },
    ],
  });

  // Submit
  const handleSubmit = useCallback(
    async (listParams?: { page?: number; pageSize?: number }) => {
      const page = listParams?.page || pagination.page;
      const pageSize = listParams?.pageSize || pagination.pageSize;
      const params = {
        page,
        pageSize,
        exporterId: session.currentData?.row.exporter.id,
        paymentType: "SUBSCRIPTION" as PaymentType,
      };

      try {
        await getPayments(params).unwrap();

        setPagination({
          page,
          pageSize,
        });
      } catch (e: any) {
        const message = Array.isArray(e.data.message)
          ? e.data.message[0]
          : e.data.message;

        if (e.data.statusCode !== 404) {
          alert.showAlert({ type: "error", message });
        }
      }
    },
    [
      alert,
      getPayments,
      pagination.page,
      pagination.pageSize,
      session.currentData?.row.exporter.id,
    ]
  );

  // Pagination;
  const handlePaginationClick = (page: number, pageSize: number) => {
    handleSubmit({
      page,
      pageSize,
    });
  };

  const handlePaymentNoClick = async (payment: any) => {
    const displayPrice = calculateDisplayPrice(
      Number(payment.price),
      payment.currencyUnit,
      Number(payment?.exchangeRate)
    );
    
    const unitPrice = calculateUnitPrice(displayPrice, payment.currencyUnit);
    const tax = calculateTax(displayPrice, unitPrice);

    openInvoiceInNewWindow({
      title: "INVOICE",
      invoiceColumnTitle: "SUBSCRIPTION INFORMATION",
      type: "subscription",
      invoiceNo: payment.paymentNo,
      invoiceDate:
        getFormattedTimezoneDateWithOffset({
          date: payment.createdAt,
          gmtOffset: session.currentData?.row.exporter.gmtOffset || 0,
        }) || "-",
      buyer: payment.exporter.companyName,
      buyerAddress: `${[
        session.currentData?.row?.exporter.postalCode || "",
        session.currentData?.row?.exporter.streetAddress || "",
        session.currentData?.row?.exporter.locality || "",
        session.currentData?.row?.exporter.region || "",
      ]
        .filter(Boolean)
        .join(", ")}`,
      buyerContact: `${session.currentData?.row?.exporter.telPrefix}) ${session.currentData?.row?.exporter.tel}`,
      buyerCountry:
        countryList
          .find(
            (item) =>
              item.codeItemName ===
              session.currentData?.row.exporter.countryCodeItemName
          )
          ?.codeItemNameEn.toUpperCase() || "-",
      paymentDate: getFormattedTimezoneDateWithOffset({
        date: payment.createdAt,
        gmtOffset: session.currentData?.row.exporter.gmtOffset || 0,
        format: DATE_FORMAT_STRINGS.YYYY_MM_DD_HH_mm,
      }),
      currencyUnit: payment.currencyUnit as string,
      items: [
        {
          description: payment.subscription.plan.name,
          unitPrice,
          tax,
          amount: displayPrice,
        },
      ],
      period: `
      ${getFormattedTimezoneDateWithOffset({
        date: payment.subscription.subscriptionStartAtFrom,
        gmtOffset: session.currentData?.row.exporter.gmtOffset || 0,
      })}–${getFormattedTimezoneDateWithOffset({
        date: payment.subscription.originalSubscriptionEndAtTo,
        gmtOffset: session.currentData?.row.exporter.gmtOffset || 0,
      })}
      `,
      total: {
        unitPrice,
        tax,
        totalPrice: displayPrice,
      },
    });
  };

  // Loading
  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef?.current?.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  useEffect(() => {
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      title={t("subscriptionManagement:subscription.paymentDetails")}
      open={open}
      onOpenChange={onOpenChange}
      width={1000}
    >
      <TableContainer>
        <Table
          ref={gridRef}
          rowData={list}
          columnDefs={columnDefs}
          isPaginationDataMaping
          totalPage={count}
          pageSize={pagination.pageSize}
          page={pagination.page}
          handlePaginationClick={handlePaginationClick}
          onGridReady={() => {
            setIsReady(true);
          }}
          components={{
            ...createUnderLineLinkAction(handlePaymentNoClick, "paymentNo"),
          }}
        />
      </TableContainer>
    </Dialog>
  );
}

export default PaymentDetailDialog;

const TableContainer = styled.div`
  border: 1px solid ${colorSet.gray9};
  border-radius: 8px;
`;
